import { GuideArticle, ContentBlock, GuideSection, GuideStep, PhotoComparePair } from '../../data/guide-articles';

export type VideoBlock = Extract<ContentBlock, { type: 'video' }>;
export type ShotBlock = Extract<ContentBlock, { type: 'shot' }>;

/** The one picture a slide shows at its top. */
export type SlideMediaItem =
  | { kind: 'video'; block: VideoBlock }
  | { kind: 'shot'; block: ShotBlock }
  | { kind: 'compare'; pair: PhotoComparePair }
  // The guide's cover illustration, for the Overview.
  | { kind: 'cover'; src: string; alt: string };

export interface GuideSlide {
  /** Unique within the guide: a heading can span several slides. */
  key: string;
  heading: string;
  /** The `##` a `###` slide sits under, so the reader keeps their place. */
  parent?: string;
  /** Every slide has a picture. A heading with none gets no slide. */
  media: SlideMediaItem;
  /** The words under the picture, with no picture left in them. */
  text: ContentBlock[];
}

export async function fetchGuideArticle(category: string, slug: string): Promise<GuideArticle | null> {
  const res = await fetch(`/data/guides/${category}--${slug}.json`);
  if (!res.ok) return null;
  return res.json();
}

/** Every picture in these blocks, in order, including inside a Step or a Note. */
function collectMedia(content: ContentBlock[]): SlideMediaItem[] {
  return content.flatMap((b): SlideMediaItem[] => {
    switch (b.type) {
      case 'video':
        return [{ kind: 'video', block: b }];
      case 'shot':
        return [{ kind: 'shot', block: b }];
      case 'photoCompare':
        return b.pairs.map((pair) => ({ kind: 'compare' as const, pair }));
      case 'steps':
        return b.steps.flatMap((step) => collectMedia(step.content));
      case 'note':
        return collectMedia(b.content);
      default:
        return [];
    }
  });
}

/**
 * The blocks with every picture taken out, at any depth. A Step keeps its
 * title even when its only content was a screenshot, so the numbered steps
 * still read in order; a Note left empty is dropped.
 */
export function stripMedia(content: ContentBlock[]): ContentBlock[] {
  return content.flatMap((b): ContentBlock[] => {
    switch (b.type) {
      case 'video':
      case 'shot':
      case 'photoCompare':
        return [];
      case 'steps':
        return [{ ...b, steps: b.steps.map((step) => ({ ...step, content: stripMedia(step.content) })) }];
      case 'note': {
        const inner = stripMedia(b.content);
        return inner.length > 0 ? [{ ...b, content: inner }] : [];
      }
      default:
        return [b];
    }
  });
}

/** A heading's content read front to back: words, and pictures where they sit. */
type Piece =
  | { kind: 'block'; block: ContentBlock }
  | { kind: 'step'; step: GuideStep; number: number }
  | { kind: 'picture'; media: SlideMediaItem };

function pieces(content: ContentBlock[]): Piece[] {
  return content.flatMap((b): Piece[] => {
    if (b.type === 'steps') {
      const first = b.start ?? 1;
      return b.steps.flatMap((step, i): Piece[] => [
        { kind: 'step', step: { ...step, content: stripMedia(step.content) }, number: first + i },
        ...collectMedia(step.content).map((media): Piece => ({ kind: 'picture', media })),
      ]);
    }
    const words = stripMedia([b]);
    return [
      ...words.map((block): Piece => ({ kind: 'block', block })),
      ...collectMedia([b]).map((media): Piece => ({ kind: 'picture', media })),
    ];
  });
}

/** Words back into blocks: steps that sit together become one numbered list again. */
function toBlocks(words: Word[]): ContentBlock[] {
  const out: ContentBlock[] = [];
  for (const w of words) {
    if (w.kind === 'block') {
      out.push(w.block);
      continue;
    }
    const last = out[out.length - 1];
    if (last?.type === 'steps' && (last.start ?? 1) + last.steps.length === w.number) last.steps.push(w.step);
    else out.push({ type: 'steps', steps: [w.step], ...(w.number > 1 ? { start: w.number } : {}) });
  }
  return out;
}

/** The words of a heading: every piece but its pictures. */
type Word = Exclude<Piece, { kind: 'picture' }>;

interface Part {
  media: SlideMediaItem;
  words: Word[];
}

/**
 * Cuts one heading into slides, each with one picture on top.
 *
 * Guides are written with a clip above the words it plays out, and a
 * screenshot below the words (or at the end of the step) it shows. So a clip
 * opens a slide and takes the words after it, and a screenshot closes one and
 * takes the words before it. A screenshot that lands on a slide the clip
 * already has is left out: the clip is playing those same steps. So is one
 * with no words between it and the picture before, unless it opens the
 * heading: then, like a clip, it takes the words below. Words after
 * the last picture join the last slide. A heading with no picture gives no
 * slide at all; the Full Article still has it.
 */
function splitByPicture(content: ContentBlock[]): Part[] {
  const parts: Part[] = [];
  let media: SlideMediaItem | null = null;
  let words: Word[] = [];

  const close = (picture: SlideMediaItem) => {
    parts.push({ media: picture, words });
    media = null;
    words = [];
  };

  for (const p of pieces(content)) {
    if (p.kind !== 'picture') {
      words.push(p);
    } else if (p.media.kind === 'video') {
      // Words with no picture yet, above the clip, stay with it.
      if (media) close(media);
      media = p.media;
    } else if (!media) {
      if (words.length === 0 && p.media.kind !== 'compare') {
        // A screenshot straight after another picture has no words of its
        // own; the one before already shows that spot. One that opens the
        // heading, with nothing above it, takes the words below instead.
        // A photo pair carries its own captions, so it always stands alone.
        if (parts.length === 0) media = p.media;
        continue;
      }
      close(p.media);
    } else if (words.length > 0) {
      close(media);
    }
  }

  if (media) close(media);
  else if (words.length > 0 && parts.length > 0) parts[parts.length - 1].words.push(...words);

  return parts;
}

function sectionSlides(section: GuideSection, parent?: string): GuideSlide[] {
  const own = splitByPicture(section.content).map(
    (part, i): GuideSlide => ({
      key: `${parent ?? ''}/${section.heading}/${i}`,
      heading: section.heading,
      ...(parent ? { parent } : {}),
      media: part.media,
      text: toBlocks(part.words),
    })
  );
  // Guides go two levels deep (## and ###); a deeper level is not a heading
  // the parser writes.
  const subs = parent ? [] : (section.subsections ?? []).flatMap((sub) => sectionSlides(sub, section.heading));
  return [...own, ...subs];
}

/**
 * The Overview: the tldr under the guide's own picture. That is the task
 * clip above the first heading when the guide has one, and otherwise its
 * cover illustration. With neither there is no Overview.
 */
function overviewSlide(guide: GuideArticle): GuideSlide | null {
  const intro = guide.intro ?? [];
  const text = [...stripMedia(intro), ...(guide.tldr ? [{ type: 'paragraph' as const, text: guide.tldr }] : [])];
  if (text.length === 0) return null;
  const media: SlideMediaItem | undefined =
    collectMedia(intro)[0] ?? (guide.thumbnail ? { kind: 'cover', src: guide.thumbnail, alt: guide.title } : undefined);
  if (!media) return null;
  return { key: 'overview', heading: 'Overview', media, text };
}

/**
 * Slides for the slideshow popup, in the order the guide reads. Every slide
 * has a picture at its top; a heading with nothing to show is left to the
 * Full Article.
 */
export function getGuideSlidesFromArticle(guide: GuideArticle): GuideSlide[] {
  const overview = overviewSlide(guide);
  return [...(overview ? [overview] : []), ...guide.sections.flatMap((section) => sectionSlides(section))];
}
